import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ResetFormDataAction } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import {
  AllowedCountryType,
  UiZoneWidgetConfig,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';

import { FormActionTypeEnum } from '@finxone-platform/form-action';
import { PageConfigurationProvider } from '../../../zones/page-configuration.provider';
import { FormSubmitter } from '../../widgets/form-widgets/form-submitter/form-submitter.component';
import { PARENT_FORM_ROW_GAP } from '@app/finxone-web-frontend/app/lib/utils/di-tokens';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-intl-new-currency-country-widget',
  templateUrl: './intl-new-currency-country-widget.component.html',
})
export class IntlNewCurrencyCountryComponent extends FormSubmitter implements OnInit {
  public selectCountryCurrencyForm: FormGroup;
  @Input() widgetConfig: UiZoneWidgetConfig;
  @Input() currencyCodes: { title: string; value: string; currencyFlag: string }[];
  @Input() countryList: AllowedCountryType[];
  @Input() customStyle: baseCssConfigStyle;
  @Input() continueButtonStyle: baseCssConfigStyle = {};
  @Input() continueButtonContent = 'Continue';
  @Input() showCurrencyFlags = true;
  @Input() isSubmitTypeAsynchronous = false;
  @Input() globalStylingClass = '';
  @Input() isAllowedGlobalStyling = false;
  @Input() isNewAddBeneficiaryIntlExist = false;

  @Output() formChangeEvent = new EventEmitter<{
    currency: string;
    countryIso2: string;
    isCountryCurrencyFormValid: boolean;
  }>();

  public formGroup: FormGroup<{}>;

  public selectedCountryOption: AllowedCountryType;
  public selectedCurrencyOption: { title: string; currencyFlag: string; value: string };
  public parentFormWidgetRowGap: string | undefined | null = inject(PARENT_FORM_ROW_GAP, { optional: true });

  constructor(
    private formSubmissionService: FormSubmissionService,
    private _router: Router,
    public override store: Store,
    public pageConfig: PageConfigurationProvider,
  ) {
    super(store);
  }
  ngOnInit() {
    this.buildForm();
    if (this.isSubmitTypeAsynchronous) this.emitLatestValues();
  }
  public buildForm() {
    this.subscribeToFormAction(FormActionTypeEnum.INTL_BENEFICIARY_ADD).subscribe((response) => {
      const currencyValue = response?.formData?.['currency'];
      const countryValue = response?.formData?.['countryIso2'];

      // Check if currencyKey is present and find corresponding currency title
      const selectedCurrency = currencyValue
        ? (this.currencyCodes.find((currency) => currency.value === currencyValue) ?? this.currencyCodes[0])
        : this.currencyCodes[0];

      this.selectedCurrencyOption = selectedCurrency;

      // Check if countryKey is present and find corresponding country
      const selectedCountry = countryValue
        ? (this.countryList.find((country) => country.key === countryValue) ?? this.countryList[0])
        : this.countryList[0];

      this.selectedCountryOption = selectedCountry;

      this.formGroup = new FormGroup({});
      for (const field of this.widgetConfig.attributes['field_attributes']) {
        const initialValue = field.name === 'currency' ? selectedCurrency?.value : selectedCountry?.key;
        this.formGroup.addControl(field.name, new FormControl({ value: initialValue, disabled: true }, []));
      }
    });
    this.selectCountryCurrencyForm = this.formGroup;
  }

  public onSubmit() {
    this.formSubmissionService.resetCurrentPageFormGroups(); // clear the formGroups to start the add intl beneficiary journey
    this.store.dispatch(new ResetFormDataAction()); // clear the formAction data first
    if (this.selectCountryCurrencyForm.valid) {
      const formObject = this.selectCountryCurrencyForm.value;
      this.formSubmissionService.submitIndividualForm(
        {
          currency: formObject.currency.title,
          countryIso2: formObject.country.key,
        },
        '',
        FormActionTypeEnum.INTL_BENEFICIARY_ADD,
      );

      if (this.isNewAddBeneficiaryIntlExist) {
        this._router.navigateByUrl('/zones/payments/add-beneficiary-intl#personal');
      } else {
        this._router.navigateByUrl('/zones/payments/intl-beneficiary-bank-details-personal');
      }
    } else {
      this.selectCountryCurrencyForm.markAllAsTouched();
    }
  }

  private findCountryByKeyAndAssignValue(countryInput: string) {
    return this.countryList?.find((country) => country?.key === countryInput) as AllowedCountryType;
  }

  private findCurrencyByKeyAndAssignValue(currencyInput: string) {
    return this.currencyCodes?.find((country) => country?.value === currencyInput) as {
      title: string;
      value: string;
      currencyFlag: string;
    };
  }

  public emitLatestValues() {
    if (this.isSubmitTypeAsynchronous) {
      const formObject = this.selectCountryCurrencyForm.value;

      this.selectedCountryOption = this.findCountryByKeyAndAssignValue(
        this.selectCountryCurrencyForm.value?.country,
      );

      this.selectedCurrencyOption = this.findCurrencyByKeyAndAssignValue(
        this.selectCountryCurrencyForm.value?.currency,
      );

      this.formChangeEvent.emit({
        currency: formObject?.currency?.title || formObject?.currency,
        countryIso2: formObject?.country?.key || formObject?.country,
        isCountryCurrencyFormValid: !this.formGroup.invalid,
      });
    }
  }
}
